import { useState, useEffect } from "react";
import { PageHeader, EmptyState } from "@/components/Primitives";
import { fmtDateTime } from "@/lib/format";
import api from "@/lib/api";
import { Activity, PhoneCall, StickyNote, MapPinned, CalendarPlus, Building2, Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import BulkActions from "@/components/BulkActions";
import { useAuth } from "@/context/AuthContext";

const MANUAL = ["call", "note", "visit", "meeting"];

const ICON = { call: PhoneCall, note: StickyNote, visit: MapPinned, meeting: CalendarPlus, contract: Building2, status_change: Clock, opportunity: Building2, assignment: Clock };

export default function Activities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const canDelete = !["sales_rep", "readonly"].includes(user?.role);
  const [sel, setSel] = useState(() => new Set());
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const reload = () => setItems((prev) => prev.filter((a) => !sel.has(a.id)));
  useEffect(() => {
    (async () => {
      const biz = await api.get("/businesses");
      const all = [];
      await Promise.all(biz.data.items.slice(0, 40).map(async (b) => {
        const r = await api.get(`/businesses/${b.id}/timeline`);
        r.data.items.forEach((a) => all.push({ ...a, business_name: b.name }));
      }));
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setItems(all.slice(0, 100)); setLoading(false);
    })();
  }, []);

  return (
    <div data-testid="page-activities">
      <PageHeader title="Activities" subtitle="Recent activity across all your businesses." />
      {canDelete && <BulkActions endpoint="activities" ids={[...sel]} singular="activity" title="Activities" canArchive={false} onDone={reload} clear={() => setSel(new Set())} />}
      {loading ? <div className="h-40 rounded-xl bg-white border animate-pulse" /> :
        items.length === 0 ? <div className="rounded-xl border bg-white"><EmptyState icon={Activity} title="No activity yet" hint="Log calls, notes and visits to build the timeline." /></div> : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y shadow-sm">
            {items.map((a) => {
              const Ic = ICON[a.type] || StickyNote;
              return (
                <div key={a.id} className="flex items-center gap-3 p-4">
                  {canDelete && MANUAL.includes(a.type) && <Checkbox checked={sel.has(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`select-${a.id}`} />}
                  <div className="h-9 w-9 rounded-lg bg-[#00C9E0]/10 flex items-center justify-center shrink-0"><Ic className="h-4 w-4 text-[#0094A8]" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{a.title}</p>
                    <p className="text-xs text-slate-400">{a.business_name} · by {a.created_by_name}</p>
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(a.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
